import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWS } from '../contexts/WebSocketContext';
import { agentsAPI, agentGroupsAPI, mediaAPI } from '../services/api';
import { 
  FiMonitor, 
  FiLayers, 
  FiImage, 
  FiActivity,
  FiWifi,
  FiWifiOff,
  FiClock,
  FiAlertTriangle,
  FiCheck
} from 'react-icons/fi';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [stats, setStats] = useState({
    agents: 0,
    agentGroups: 0,
    mediaFiles: 0,
    onlineAgents: 0
  });
  const [recentAgents, setRecentAgents] = useState([]);
  const [systemStatus, setSystemStatus] = useState('operational');
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // For admin users, we might want to show system-wide stats
        // For regular users, show company-specific stats
        if (user.isAdmin) {
          // Admin view - show system-wide stats
          // For now, we'll show empty stats as admin endpoints might not be implemented
          setStats({
            agents: 0,
            agentGroups: 0,
            mediaFiles: 0,
            onlineAgents: 0
          });
          setRecentAgents([]);
          setSystemStatus('operational');
          setRecentAlerts([]);
        } else if (user.compID) {
          // Regular user view - show company-specific stats
          const [agents, agentGroups, media] = await Promise.all([
            agentsAPI.getAgents(user.compID),
            agentGroupsAPI.getAgentGroups(user.compID),
            mediaAPI.getAllMedia(user.compID)
          ]);

          // Calculate online agents (last seen within 5 minutes)
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          const onlineAgents = agents.filter(agent => 
            agent.agentLastOnline && new Date(agent.agentLastOnline) > fiveMinutesAgo
          );

          setStats({
            agents: agents.length,
            agentGroups: agentGroups.length,
            mediaFiles: media.length,
            onlineAgents: onlineAgents.length
          });

          // Set recent agents (last 5)
          const sortedAgents = agents
            .sort((a, b) => {
              const aTime = a.agentLastOnline ? new Date(a.agentLastOnline) : new Date(0);
              const bTime = b.agentLastOnline ? new Date(b.agentLastOnline) : new Date(0);
              return bTime - aTime;
            })
            .slice(0, 5);
          
          setRecentAgents(sortedAgents);

          // Set system status based on agent health
          const offlineAgents = agents.length - onlineAgents.length;
          if (offlineAgents === 0) {
            setSystemStatus('operational');
          } else if (offlineAgents < agents.length / 2) {
            setSystemStatus('warning');
          } else {
            setSystemStatus('critical');
          }

          // Generate sample alerts based on agent status
          const alerts = [];
          const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
          
          agents.forEach(agent => {
            if (!agent.agentLastOnline || new Date(agent.agentLastOnline) < fifteenMinutesAgo) {
              alerts.push({
                id: `offline-${agent.agentID}`,
                type: 'warning',
                message: `Agent "${agent.agentName}" has been offline for more than 15 minutes`,
                timestamp: agent.agentLastOnline ? new Date(agent.agentLastOnline) : new Date()
              });
            }
          });

          // Add info alert for recent successful connections
          if (onlineAgents.length > 0) {
            alerts.push({
              id: 'online-summary',
              type: 'info',
              message: `${onlineAgents.length} agent${onlineAgents.length === 1 ? '' : 's'} currently online and operational`,
              timestamp: new Date()
            });
          }

          setRecentAlerts(alerts.slice(0, 5)); // Show only recent 5 alerts
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set default values on error
        setStats({
          agents: 0,
          agentGroups: 0,
          mediaFiles: 0,
          onlineAgents: 0
        });
        setRecentAgents([]);
        setSystemStatus('operational');
        setRecentAlerts([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  // Real-time updates via WebSocket
  useEffect(() => {
    const unsub1 = subscribe('device:online', () => {
      setStats(prev => ({ ...prev, onlineAgents: prev.onlineAgents + 1 }));
    });
    const unsub2 = subscribe('device:offline', () => {
      setStats(prev => ({ ...prev, onlineAgents: Math.max(0, prev.onlineAgents - 1) }));
    });
    return () => { unsub1(); unsub2(); };
  }, [subscribe]);

  const isAgentOnline = (lastOnline) => {
    if (!lastOnline) return false;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(lastOnline) > fiveMinutesAgo;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadge = (isOnline) => {
    if (isOnline) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Online</span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Offline</span>;
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return <FiAlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error': return <FiAlertTriangle className="h-5 w-5 text-red-500" />;
      case 'info': return <FiCheck className="h-5 w-5 text-blue-500" />;
      default: return <FiActivity className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {user.isAdmin ? 'Admin Dashboard' : 'Dashboard'}
        </h1>
        <p className="mt-2 text-gray-600">
          Welcome back, {user.firstName || user.username}! 
          {user.isAdmin 
            ? " Here's an overview of the entire AdvDigit system."
            : " Here's what's happening with your digital signage network."
          }
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg ${
              systemStatus === 'operational' ? 'bg-green-100' : 
              systemStatus === 'warning' ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <FiCheck className={`h-6 w-6 ${getStatusColor(systemStatus)}`} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">System Status</p>
              <p className={`text-lg font-semibold capitalize ${getStatusColor(systemStatus)}`}>
                {systemStatus}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiMonitor className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.agents}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiWifi className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Online Agents</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.onlineAgents}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FiLayers className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Agent Groups</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.agentGroups}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FiImage className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Media Files</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.mediaFiles}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Monitoring Sections */}
      {!user.isAdmin && stats.agents > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Detailed Agent Status */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Agent Status</h2>
              <a href="/devices" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Manage devices →
              </a>
            </div>
            
            {recentAgents.length === 0 ? (
              <div className="text-center py-8">
                <FiMonitor className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No agents to monitor</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentAgents.map((agent) => (
                  <div key={agent.agentID} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${isAgentOnline(agent.agentLastOnline) ? 'bg-green-100' : 'bg-red-100'}`}>
                        {isAgentOnline(agent.agentLastOnline) ? (
                          <FiWifi className="h-5 w-5 text-green-600" />
                        ) : (
                          <FiWifiOff className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{agent.agentName}</h3>
                        <p className="text-sm text-gray-500">
                          Last seen: {agent.agentLastOnline 
                            ? new Date(agent.agentLastOnline).toLocaleTimeString()
                            : 'Never'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(isAgentOnline(agent.agentLastOnline))}
                      {agent.licenseSerial && (
                        <p className="text-xs text-gray-500 mt-1">
                          License: {agent.licenseSerial}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Alerts */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Recent Alerts</h2>
            {recentAlerts.length === 0 ? (
              <div className="text-center py-8">
                <FiCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent alerts</p>
                <p className="text-sm text-gray-400 mt-1">All systems are running smoothly</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {alert.timestamp.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/devices'}>
          <div className="text-center">
            <div className="p-3 bg-blue-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <FiLayers className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Manage Devices</h3>
            <p className="text-gray-600 text-sm">
              Organize your agents by groups and manage their settings
            </p>
          </div>
        </div>

        <div className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.location.href = '/content'}>
          <div className="text-center">
            <div className="p-3 bg-purple-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <FiImage className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Manage Content</h3>
            <p className="text-gray-600 text-sm">
              Upload and organize images, videos and media files
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
